import { FormEvent, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  CircleAlert,
  Info,
  LoaderCircle,
  Mail,
  MailCheck,
  RefreshCw,
} from "lucide-react";
import AuthFrame from "../components/PublicChrome/AuthFrame";
import AuthorizationService from "../../sdk/services/AuthService";
import {
  INVALID_EMAIL_MESSAGE,
  SUCCESS_TITLE,
  SPAM_NOTE,
  SEND_FAILURE_MESSAGE,
  isResetEmail,
  resetRequestOutcome,
  resetSentSentence,
} from "./esqueciSenha/emailReset";
import "./esqueciSenha/EsqueciSenhaForm.css";

function statusOf(err: unknown): number | undefined {
  if (typeof err !== "object" || err === null || !("response" in err)) return undefined;
  const status = (err as { response?: { status?: unknown } }).response?.status;
  return typeof status === "number" ? status : undefined;
}

export default function EsqueciSenhaView() {
  const inFlightRef = useRef(false);
  const [email, setEmail] = useState("");
  const [invalid, setInvalid] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [sentEmail, setSentEmail] = useState("");

  const sendReset = async (address: string) => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    setBusy(true);
    setFailure(null);
    try {
      await AuthorizationService.forgotPassword(address);
      setSentEmail(address);
      setSent(true);
      setInvalid(false);
      setFailure(null);
    } catch (err: unknown) {
      const outcome = resetRequestOutcome(statusOf(err));
      if (outcome === "sent") {
        setSentEmail(address);
        setSent(true);
        setInvalid(false);
        setFailure(null);
      } else if (outcome === "invalid") {
        setInvalid(true);
        setFailure(null);
      } else {
        setFailure(SEND_FAILURE_MESSAGE);
      }
    } finally {
      inFlightRef.current = false;
      setBusy(false);
    }
  };

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (inFlightRef.current) return;

    const trimmed = email.trim();
    if (!isResetEmail(trimmed)) {
      setInvalid(true);
      setFailure(null);
      return;
    }

    void sendReset(trimmed);
  };

  const onResend = () => {
    if (inFlightRef.current) return;
    void sendReset(sentEmail);
  };

  const onEmailChange = (value: string) => {
    setEmail(value);
    setInvalid(false);
    setFailure(null);
  };

  const loginLink = (
    <Link className="esqueci-form__switch" to="/login">
      <span className="esqueci-form__switch-quiet">Lembrou a senha?</span>
      <span className="esqueci-form__switch-link">
        Entrar <ArrowRight size={18} aria-hidden="true" />
      </span>
    </Link>
  );

  return (
    <AuthFrame screen="esqueci-senha">
      {sent ? (
        <div className="esqueci-form">
          <div className="esqueci-form__seal" aria-hidden="true">
            <MailCheck size={30} />
          </div>
          <div className="esqueci-form__status" role="status">
            <h2 className="esqueci-form__status-title">{SUCCESS_TITLE}</h2>
            <p className="esqueci-form__status-body">{resetSentSentence(sentEmail)}</p>
          </div>
          <p className="esqueci-form__note">
            <Info size={20} aria-hidden="true" />
            <span>{SPAM_NOTE}</span>
          </p>
          {failure ? (
            <p className="esqueci-form__alert" role="alert">{failure}</p>
          ) : null}
          <button
            className="esqueci-form__resend"
            type="button"
            disabled={busy}
            aria-busy={busy || undefined}
            onClick={onResend}
          >
            {busy ? (
              <><LoaderCircle className="esqueci-form__spinner" size={20} aria-hidden="true" />Enviando…</>
            ) : (
              <><RefreshCw size={20} aria-hidden="true" />Reenviar link</>
            )}
          </button>
          {loginLink}
        </div>
      ) : (
        <div className="esqueci-form">
          <form noValidate aria-busy={busy} onSubmit={onSubmit}>
            <h1 className="esqueci-form__title">Esqueci a senha</h1>
            <p className="esqueci-form__subtitle">Informe o e-mail da sua conta. Vamos enviar um link para você criar uma senha nova.</p>
            <div className="esqueci-form__field">
              <label className="esqueci-form__label" htmlFor="esqueci-email">
                E-mail
              </label>
              <div
                className={
                  "esqueci-form__control" + (invalid ? " is-invalid" : "")
                }
              >
                <Mail
                  className="esqueci-form__icon"
                  size={20}
                  aria-hidden="true"
                />
                <input
                  id="esqueci-email"
                  className="esqueci-form__input"
                  name="email"
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  placeholder="seu@email.com"
                  value={email}
                  onChange={(event) => onEmailChange(event.target.value)}
                  disabled={busy}
                  aria-invalid={invalid || undefined}
                  aria-describedby={invalid ? "esqueci-email-error" : undefined}
                />
              </div>
              {invalid ? (
                <p className="esqueci-form__error" id="esqueci-email-error">
                  <CircleAlert size={16} aria-hidden="true" />
                  <span>{INVALID_EMAIL_MESSAGE}</span>
                </p>
              ) : null}
            </div>
            {failure ? (
              <p className="esqueci-form__alert" role="alert">{failure}</p>
            ) : null}
            <button className="esqueci-form__submit" type="submit" disabled={busy}>
              {busy ? (
                <><LoaderCircle className="esqueci-form__spinner" size={20} aria-hidden="true" />Enviando…</>
              ) : (
                "Enviar link"
              )}
            </button>
            <hr className="esqueci-form__rule" />
            {loginLink}
          </form>
        </div>
      )}
    </AuthFrame>
  );
}
